import { match, P } from 'ts-pattern';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItem, ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import type {
  IxcMutualfundsV1FundOrderAmount,
  IxcMutualfundsV1FundOrderAmountConversion,
  IxcMutualfundsV1FundOrderData,
  IxcMutualfundsV1FundOrderExecution,
  IxcMutualfundsV1FundOrderInstruction,
  IxcMutualfundsV1FundOrderInstructionTaxLotSelection,
  IxcMutualfundsV1FundOrderSpec,
  IxcMutualfundsV1FundOrderState,
  IxcMutualfundsV1FundTransaction,
} from '@interxfi/types';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { getBooleanFromUndefinedOrNullable, getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { getAssetStateListItems } from '../../asset/asset-list-items';
import { getAssetDetailPath } from '../../asset/asset-routing';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getOrderExtensionListItems, getOrderMiddleMoneyListItemsWithSummary } from '../../order/order-list-items';
import { getPortfolioHoldDetailPath } from '../../portfolio/hold/portfolio-hold-routing';
import { getPortfolioTaxLotAllocationListItems } from '../../portfolio/transaction/portfolio-transaction-list-items';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getProgramDetailPath } from '../../userauth/program/program-routing';
import { getMutualFundsVenueOrderDetailPath } from '../venue-order/mutual-funds-venue-order-routing';
import { MutualFundsFundOrderActionPill } from './mutual-funds-fund-order-action-pill';
import { getMutualFundsFundOrderDetailPath } from './mutual-funds-fund-order-routing';
import { MutualFundsFundOrderStatusPill } from './mutual-funds-fund-order-status-pill';

export function getMutualFundsOrderAmountListItems(data: IxcMutualfundsV1FundOrderAmount | undefined, prefix = '') {
  const typeNameItem = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcMutualfundsV1',
      oneOfName: 'IxcMutualfundsV1FundOrderAmount',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameItem;
  }

  if ('notional' in data) {
    return {
      ...typeNameItem,
      notional: {
        '!type': 'notional',
        value: getReadonlyListItemCurrency(`${prefix}notional.value`, 'Notional Amount', getNumericStringOrUndefinedFromNullable(data, 'notional')),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('quantity' in data) {
    return {
      ...typeNameItem,
      quantity: {
        '!type': 'quantity',
        value: getReadonlyListItemNumber(`${prefix}quantity.value`, 'Quantity', getNumericStringOrUndefinedFromNullable(data, 'quantity')),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameItem;
}

export function getMutualFundsFundOrderSpecListItems(data: IxcMutualfundsV1FundOrderSpec | undefined, prefix = '') {
  return {
    timestamp: getReadonlyListItemDate(`${prefix}timestamp`, 'Requested At', data?.timestamp, 'timestamp_iso_ms'),
    action: getReadonlyListItemTranslatedString(`${prefix}action`, 'Action', {
      namespace: 'ixcMutualfundsV1',
      enumName: 'IxcMutualfundsV1FundOrderAction',
      value: data?.action,
    }),
    amount: getMutualFundsOrderAmountListItems(data?.amount, `${prefix}amount.`),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    programId: getReadonlyListItemId(`${prefix}programId`, 'Program ID', data?.programId, getProgramDetailPath(data?.programId)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getMutualFundsFundOrderExecutionListItems(data: IxcMutualfundsV1FundOrderExecution | undefined, prefix = '') {
  return {
    executionId: getReadonlyListItemId(`${prefix}executionId`, 'Execution ID', data?.executionId),
    venue: getReadonlyListItemTranslatedString(`${prefix}venue`, 'Venue', {
      namespace: 'ixcMutualfundsV1',
      enumName: 'IxcMutualfundsV1FundVenue',
      value: data?.venue,
    }),
    nav: getReadonlyListItemCurrency(`${prefix}nav`, 'NAV', getNumericStringOrUndefinedFromNullable(data, 'nav')),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', getNumericStringOrUndefinedFromNullable(data, 'quantity')),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', getNumericStringOrUndefinedFromNullable(data, 'amount')),
    settlementTime: data?.settlementTime
      ? getReadonlyListItemDate(`${prefix}settlementTime`, 'Settlement Time', data.settlementTime, 'timestamp_iso_ms')
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getMutualFundsFundOrderInstructionTaxLotSelectionListItems(
  data: IxcMutualfundsV1FundOrderInstructionTaxLotSelection | undefined,
  prefix = '',
) {
  const typeNameItem = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcMutualfundsV1',
      oneOfName: 'IxcMutualfundsV1FundOrderInstructionTaxLotSelection',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameItem;
  }

  if ('wellKnown' in data) {
    return {
      ...typeNameItem,
      wellKnown: {
        '!type': 'wellKnown',
        value: {
          method: getReadonlyListItemTranslatedString(`${prefix}wellKnown.method`, 'Method', {
            namespace: 'ixcPortfolioV1',
            enumName: 'IxcPortfolioV1TaxLotMethod',
            value: data.wellKnown.method,
          }),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('custom' in data) {
    return {
      ...typeNameItem,
      custom: {
        '!type': 'custom',
        value: {
          allocations: data.custom.allocations.map((allocation, index) =>
            getPortfolioTaxLotAllocationListItems(allocation, `${prefix}custom.allocations.${index}.`),
          ),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameItem;
}

export function getMutualFundsFundOrderInstructionListItems(data: IxcMutualfundsV1FundOrderInstruction | undefined, prefix = '') {
  return {
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    action: getReadonlyListItemTranslatedString(`${prefix}action`, 'Action', {
      namespace: 'ixcMutualfundsV1',
      enumName: 'IxcMutualfundsV1FundOrderAction',
      value: data?.action,
    }),
    actionPill: getReadonlyListItemPill(
      `${prefix}actionPill`,
      'Action',
      data?.action ? <MutualFundsFundOrderActionPill action={data.action} /> : null,
    ),
    amount: getMutualFundsOrderAmountListItems(data?.amount, `${prefix}amount.`),
    taxLotSelection: (data?.taxLotSelection
      ? getMutualFundsFundOrderInstructionTaxLotSelectionListItems(data.taxLotSelection, `${prefix}taxLotSelection.`)
      : undefined) as ReturnType<typeof getMutualFundsFundOrderInstructionTaxLotSelectionListItems> | undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getMutualFundsFundOrderAmountConversionListItems(data: IxcMutualfundsV1FundOrderAmountConversion | undefined, prefix = '') {
  return {
    amount: getMutualFundsOrderAmountListItems(data?.amount, `${prefix}amount.`),
    reason: getReadonlyListItemTranslatedString(`${prefix}reason`, 'Reason', {
      namespace: 'ixcMutualfundsV1',
      enumName: 'IxcMutualfundsV1FundOrderAmountConversionReason',
      value: data?.reason,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

interface MutualFundKeyLinkContext {
  accountId?: boolean;
  assetId?: boolean;
  fundOrderId?: boolean;
  programId?: boolean;
  venueOrderId?: boolean;
}

export function getMutualFundsFundOrderDataListItems(
  data: IxcMutualfundsV1FundOrderData | undefined,
  accountId: string | undefined,
  linkContext: MutualFundKeyLinkContext,
  prefix = '',
) {
  return {
    instruction: getMutualFundsFundOrderInstructionListItems(data?.instruction, `${prefix}instruction.`),
    extension: data?.execution ? getOrderExtensionListItems(data?.extension, `${prefix}extension.`) : undefined,
    asset: getAssetStateListItems(data?.asset, `${prefix}asset.`),
    estimatedNav: getReadonlyListItemCurrency(
      `${prefix}extimatedNav`,
      'Estimated NAV',
      getNumericStringOrUndefinedFromNullable(data, 'estimatedNav'),
    ),
    amountConversion: data?.amountConversion
      ? getMutualFundsFundOrderAmountConversionListItems(data.amountConversion, `${prefix}amountConversion.`)
      : undefined,
    holdId: data?.holdId
      ? getReadonlyListItemId(`${prefix}holdId`, 'Hold ID', data.holdId, getPortfolioHoldDetailPath(accountId, data?.holdId))
      : undefined,
    venueOrderId: data?.venueOrderId
      ? getReadonlyListItemId(
          `${prefix}venueOrderId`,
          'Venue Order ID',
          data.venueOrderId,
          linkContext.venueOrderId ? getMutualFundsVenueOrderDetailPath(data.venueOrderId) : undefined,
        )
      : undefined,
    execution: data?.execution ? getMutualFundsFundOrderExecutionListItems(data.execution, `${prefix}execution.`) : undefined,
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    outcome: data?.outcome
      ? getReadonlyListItemTranslatedString(`${prefix}outcome`, 'Outcome', {
          namespace: 'ixcMutualfundsV1',
          enumName: 'IxcMutualfundsV1FundOrderOutcome',
          value: data?.outcome,
        })
      : undefined,
    failureReason: data?.failureReason ? getReadonlyListItemString(`${prefix}failureReason`, 'Failure Reason', data.failureReason) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getMutualFundsFundOrderStateListItems(
  data: IxcMutualfundsV1FundOrderState | undefined,
  linkContext: MutualFundKeyLinkContext = {
    accountId: true,
    fundOrderId: false,
    programId: true,
    assetId: true,
    venueOrderId: false,
  },
) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    fundOrderId: getReadonlyListItemId(
      'fundOrderId',
      'Fund Order ID',
      data?.fundOrderId,
      linkContext.fundOrderId ? getMutualFundsFundOrderDetailPath(data?.fundOrderId) : undefined,
    ),
    accountId: getReadonlyListItemId(
      'accountId',
      'Account ID',
      data?.accountId,
      linkContext.accountId ? getRegistrationAccountDetailPath(data?.accountId) : undefined,
    ),
    programId: getReadonlyListItemId(
      'programId',
      'Program ID',
      data?.programId,
      linkContext.programId ? getProgramDetailPath(data?.programId) : undefined,
    ),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcMutualfundsV1',
      enumName: 'IxcMutualfundsV1FundOrderStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <MutualFundsFundOrderStatusPill status={data.status} /> : null),
    data: getMutualFundsFundOrderDataListItems(data?.data, data?.accountId, linkContext, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getMutualFundTransactionListItems(data: IxcMutualfundsV1FundTransaction | undefined, prefix = '') {
  return {
    fundOrderId: getReadonlyListItemId(`${prefix}fundOrderId`, 'Order ID', data?.fundOrderId, getMutualFundsFundOrderDetailPath(data?.fundOrderId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    venue: getReadonlyListItemTranslatedString(`${prefix}venue`, 'Venue', {
      namespace: 'ixcMutualfundsV1',
      enumName: 'IxcMutualfundsV1FundVenue',
      value: data?.venue,
    }),
    confirmedAt: getReadonlyListItemDate(`${prefix}confirmedAt`, 'Confirmed At', data?.confirmedAt, 'timestamp_iso_ms'),
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    action: getReadonlyListItemTranslatedString(`${prefix}action`, 'Action', {
      namespace: 'ixcMutualfundsV1',
      enumName: 'IxcMutualfundsV1FundOrderAction',
      value: data?.action,
    }),
    actionPill: getReadonlyListItemPill(
      `${prefix}actionPill`,
      'Action',
      data?.action ? <MutualFundsFundOrderActionPill action={data.action} /> : null,
    ),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    nav: getReadonlyListItemCurrency(`${prefix}nav`, 'NAV', data?.nav),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    netAmount: getReadonlyListItemCurrency(`${prefix}netAmount`, 'Net Amount', data?.netAmount),
    solicited: getReadonlyListItemYesOrNo(`${prefix}solicited`, 'Solicited', getBooleanFromUndefinedOrNullable(data, 'solicited')),
    trailer: getReadonlyListItemString(`${prefix}trailer`, 'Trailer', data?.trailer),
    capacity: getReadonlyListItemTranslatedString(`${prefix}capacity`, 'Capacity', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Capacity',
      value: data?.capacity,
    }),
    // Preferably show a table for middle money or use the key-value pair list
    ...getOrderMiddleMoneyListItemsWithSummary(data?.middleMoney, `${prefix}middleMoney.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getMutualFundsFundOrderDetailPageListItems(data: IxcMutualfundsV1FundOrderState | undefined, linkContext: MutualFundKeyLinkContext) {
  const listItems = getMutualFundsFundOrderStateListItems(data, linkContext);

  const leftColumn: ReadonlyListGroup[] = [
    {
      id: 'main',
      items: [
        listItems.data.asset?.summary || listItems.data.instruction.symbol,
        listItems.data.instruction.action,
        ...(listItems.data.execution
          ? [listItems.data.execution.amount, listItems.data.execution.quantity, listItems.data.execution.nav, listItems.data.execution.venue]
          : [
              match(listItems.data.instruction.amount)
                .with({ quantity: P.not(P.nullish) }, (a) => a.quantity.value)
                .with({ notional: P.not(P.nullish) }, (a) => a.notional.value)
                .otherwise(() => getReadonlyListItemString('amount', !data ? <Skeleton charCount={6} type="line" /> : 'Amount', undefined)),
              listItems.data.estimatedNav,
            ]),
        ...(listItems.data.amountConversion
          ? [
              match(listItems.data.amountConversion.amount)
                .with({ quantity: P.not(P.nullish) }, (a) => ({ ...a.quantity.value, title: 'Converted Quantity' }))
                .with({ notional: P.not(P.nullish) }, (a) => ({ ...a.notional.value, title: 'Converted Notional Value' }))
                .otherwise(() => getReadonlyListItemString('amount', !data ? <Skeleton charCount={6} type="line" /> : 'Converted Amount', undefined)),
              { ...listItems.data.amountConversion.reason, title: 'Amount Conversion Reason' },
            ]
          : []),
        ...(listItems.data.outcome ? [listItems.data.outcome] : []),
        ...(listItems.data.failureReason ? [listItems.data.failureReason] : []),
        listItems.data.tradeDate,
        listItems.data.settlementDate,
        ...(listItems.data.execution?.settlementTime ? [listItems.data.execution.settlementTime] : []),
      ],
    },
  ];

  if (listItems.data.extension) {
    leftColumn.push({
      id: 'extension',
      items: [
        listItems.data.extension.solicited,
        listItems.data.extension.trailer,
        ...(listItems.data.extension.commission
          ? [
              listItems.data.extension.commission.value.typeName,
              ...match(listItems.data.extension.commission)
                .with({ '!type': 'fixed' }, (c) => [c.value.amount])
                .with({ '!type': 'simpleRate' }, (c) => {
                  const base = [c.value.rate, c.value.basis];

                  if (c.value.minimum) {
                    base.push(c.value.minimum);
                  }

                  if (c.value.maximum) {
                    base.push(c.value.maximum);
                  }

                  return base;
                })
                .otherwise(() => []),
            ]
          : []),
      ],
    });
  }

  return {
    left: leftColumn,
    right: [
      listItems.metadata.createdAt,
      listItems.metadata.updatedAt,
      listItems.fundOrderId,
      listItems.accountId,
      listItems.programId,
      ...(listItems.data.holdId ? [listItems.data.holdId] : []),
      ...(listItems.data.venueOrderId ? [listItems.data.venueOrderId] : []),
      ...(listItems.data.execution ? [listItems.data.execution.executionId] : []),
    ] as const satisfies ReadonlyListItem[],
  };
}
